import React from "react";
import Pagination from "../../helpers/Pagination";
import { ArrowRight } from "lucide-react";
import { FaFilter } from "react-icons/fa";

const formatDateTime = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};

const formatDuration = (hours = 0) => {
  const days = Math.floor(hours / 24);

  const months = Math.floor(days / 30);

  if (months > 0) {
    return `${months}M ${days % 30}D`;
  }

  if (days > 0) {
    return `${days}D ${hours % 24}H`;
  }

  return `${hours}H`;
};

export default function StageHistoryTable({
  data = [],
  pagination,
  onPageChange,
  onItemsPerPageChange,
  activeFiltersCount = 0,
  onFilterClick,
  onExportExcel,
  onExportPDF,
}) {
  
  return (
    <div className="bg-white rounded-2xl overflow-hidden">

      {/* Header */}

    <div className="px-5 py-4 bg-white flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h2 className="text-lg font-semibold">
          Stage Movement History
        </h2>

        <p className="text-sm text-gray-500">
          Complete audit trail of stage changes
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">

        <button
          onClick={onFilterClick}
          className="
            relative
            flex items-center gap-2
            px-3 py-3            
            rounded-lg
           bg-red-100
          "
        >
          <FaFilter className="text-sm" />

          {activeFiltersCount > 0 && (
            <span
              className="
                absolute
                -top-2
                -right-2
                bg-red-600
                text-white
                text-xs
                w-5
                h-5
                rounded-full
                flex
                items-center
                justify-center
              "
            >
              {activeFiltersCount}
            </span>
          )}
        </button>

        <button
          onClick={onExportExcel}
          className="px-4 py-2 bg-green-600 text-white rounded-lg"
        >
          Excel
        </button>

        <button
          onClick={onExportPDF}
          className="px-4 py-2 bg-red-600 text-white rounded-lg"
        >
          PDF
        </button>

      </div>
    </div>

      {/* Table */}

      <div className="overflow-x-auto rounded-md">

        <table className="min-w-full bg-white border-separate border-spacing-y-2 px-2">

        <thead className="text-sm text-gray-600">
          <tr className="text-center rounded-lg shadow-sm bg-red-100">

              <th className="py-4 px-4 rounded-l-lg font-semibold">
                Client
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold">
                Contact
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold">
                Stage Movement
              </th>

              <th className="px-4 py-3 text-left text-sm font-semibold">
                Changed By
              </th>

              <th className="px-4 py-3 text-center text-sm font-semibold">
                Duration
              </th>

              <th className="px-4 py-3 text-center text-sm font-semibold">
                Changed At
              </th>

              <th className="py-4 px-4 rounded-r-lg font-semibold">
                Remarks
              </th>

            </tr>

          </thead>

          <tbody>

            {data.length > 0 ? (
              data.map(
                (row, index) => (
                  <tr
                    key={index}
                    className="bg-gray-50 hover:bg-gray-100 text-sm rounded-lg shadow-sm text-center"
                  >
                    {/* Client */}

                    <td className="py-3 px-4 font-medium text-gray-700 rounded-l-lg">

                      <div>

                        <p className="font-semibold text-gray-900">
                          {
                            row.clientName
                          }
                        </p>

                        <p className="text-xs text-gray-500">
                          {
                            row.clientId
                          }
                        </p>

                      </div>

                    </td>

                    <td className="px-4 py-4">
                        <p className="text-xs text-gray-500">
                          {
                            row.contactName
                          }
                        </p>

                        <p className="text-xs text-gray-500">
                          {
                            row.contactPhone
                          }
                        </p>

                    </td>
                    {/* Movement */}

                    <td className="px-4 py-4">

                      <div className="flex items-center gap-2 flex-wrap">

                        <span
                          className="
                          px-3
                          py-1
                          rounded-full
                          bg-red-50
                          text-red-700
                          text-xs
                          font-medium
                        "
                        >
                          {
                            row.fromStageName
                          }
                        </span>

                        <ArrowRight
                          size={16}
                        />

                        <span
                          className="
                          px-3
                          py-1
                          rounded-full
                          bg-green-50
                          text-green-700
                          text-xs
                          font-medium
                        "
                        >
                          {
                            row.toStageName
                          }
                        </span>

                      </div>

                    </td>

                    {/* User */}

                    <td className="px-4 py-4">

                      <div>

                        <p className="font-medium">
                          {
                            row
                              .changedBy
                              ?.name
                          }
                        </p>

                      </div>

                    </td>

                    {/* Duration */}

                    <td className="px-4 py-4 text-center">

                      <span
                        className="
                        px-2
                        py-1
                        rounded-full
                        bg-blue-50
                        text-blue-700
                        text-xs
                        font-medium
                      "
                      >
                        {formatDuration(
                          row.durationInPreviousStageHours
                        )}
                      </span>

                    </td>

                    {/* Date */}

                    <td className="px-4 py-4 text-center text-sm">

                      {formatDateTime(
                        row.changedAt
                      )}

                    </td>

                    {/* Remarks */}

                    <td className="px-4 py-4 text-sm text-gray-600 rounded-r-lg">

                      {row.remarks ||
                        "-"}

                    </td>

                  </tr>
                )
              )
            ) : (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-10 text-gray-500"
                >
                  No stage history found
                </td>
              </tr>
            )}

          </tbody>

        </table>

      </div>

      {/* Pagination */}

      {pagination?.total > 0 && (
        <div className="p-4">

          <Pagination
            totalItems={
              pagination.total
            }
            itemsPerPage={
              pagination.limit
            }
            currentPage={
              pagination.page
            }
            onPageChange={
              onPageChange
            }
            onItemsPerPageChange={
              onItemsPerPageChange
            }
          />

        </div>
      )}

    </div>
  );
}